import { v4 } from "uuid";

import {
  RuleSet,
  Operator,
  FieldName,
  TimeFrame,
  RuleSetDecision,
  Rule
} from "./types";
import Equal from "./assets/equals.svg?react";
import NotEqual from "./assets/not-equals.svg?react";
import Chevron from "./assets/chevron.svg?react";
import { GroupTypeLabel } from "../components/GroupTypeLabel";

const fieldTypeTimeFrameOptions = [
  {
    label: "within 1 year",
    value: TimeFrame.Within1Year
  },
  {
    label: "within 2 years",
    value: TimeFrame.Within2Years
  },
  {
    label: "within 3 years",
    value: TimeFrame.Within3Years
  },
  {
    label: "within 4 years",
    value: TimeFrame.Within4Years
  },
  {
    label: "within 5 years",
    value: TimeFrame.Within5Years
  },
  {
    label: "within 6 years",
    value: TimeFrame.Within6Years
  },
  {
    label: "within 7 years",
    value: TimeFrame.Within7Years
  },
  {
    label: "within 8 years",
    value: TimeFrame.Within8Years
  },
  {
    label: "within 9 years",
    value: TimeFrame.Within9Years
  },
  {
    label: "within 10 years",
    value: TimeFrame.Within10Years
  }
];

export const fieldTypeOperatorOptions = [
  {
    label: (
      <div className="flex gap-2 items-center">
        <Chevron className="h-4 w-4 shrink-0 stroke-inherit" />
        Greater than
      </div>
    ),
    value: Operator.GreaterThan
  },
  {
    label: (
      <div className="flex gap-2 items-center">
        <Chevron className="h-4 w-4 shrink-0 stroke-inherit transform rotate-180" />
        Less than
      </div>
    ),
    value: Operator.LessThan
  },
  {
    label: (
      <div className="flex gap-2 items-center stroke-inherit text-inherit">
        <Equal className="h-4 w-4 shrink-0 stroke-inherit text-inherit" />
        Equals
      </div>
    ),
    value: Operator.Equal
  },
  {
    label: (
      <div className="flex gap-2 items-center">
        <NotEqual className="h-4 w-4 shrink-0 stroke-inherit" />
        Doesn&lsquo;t equal
      </div>
    ),
    value: Operator.NotEqual
  }
];

export const fieldNameOptions = [
  {
    label: "Income",
    options: [
      {
        label: <GroupTypeLabel title="Gross income to rent" group />,
        value: FieldName.GrossIncomeToRentRatio
      },
      {
        label: <GroupTypeLabel title="Gross income" group />,
        value: FieldName.GrossIncome
      },
      {
        label: <GroupTypeLabel title="Net income" group />,
        value: FieldName.NetIncome
      },
      {
        label: <GroupTypeLabel title="Debt to net income" group />,
        value: FieldName.DebtToNetIncome
      }
    ]
  },
  {
    label: "Credit score",
    options: [
      {
        label: <GroupTypeLabel title="No credit score" />,
        value: FieldName.NoCreditScore
      },
      {
        label: <GroupTypeLabel title="Credit score" />,
        value: FieldName.CreditScore
      },
      {
        label: <GroupTypeLabel title="Past due accounts" />,
        value: FieldName.PastDueAccount
      },
      {
        label: <GroupTypeLabel title="Balance past due" />,
        value: FieldName.UnpaidCollectionsBalance
      },
      {
        label: <GroupTypeLabel title="Balance of collections" />,
        value: FieldName.CollectionsBalance
      },
      {
        label: <GroupTypeLabel title="Bankruptcy records" />,
        value: FieldName.BankruptcyRecords
      }
    ]
  },
  {
    label: "Housing court",
    options: [
      {
        label: <GroupTypeLabel title="Housing court records" />,
        value: FieldName.HousingCourtRecords
      }
    ]
  },
  {
    label: "Criminal history",
    options: [
      {
        label: <GroupTypeLabel title="Sex offender records" />,
        value: FieldName.SexOffenderCheck
      },
      {
        label: <GroupTypeLabel title="Felony records" />,
        value: FieldName.FelonyRecords
      }
    ]
  }
];

// TODO: likely redundant
export const fieldOperators = {
  [FieldName.HousingCourtRecords]: fieldTypeOperatorOptions,
  [FieldName.BankruptcyRecords]: fieldTypeOperatorOptions,
  [FieldName.SexOffenderCheck]: fieldTypeOperatorOptions,
  [FieldName.PastDueAccount]: fieldTypeOperatorOptions,
  [FieldName.UnpaidCollectionsBalance]: fieldTypeOperatorOptions,
  [FieldName.CreditScore]: fieldTypeOperatorOptions,
  [FieldName.NetIncome]: fieldTypeOperatorOptions,
  [FieldName.GrossIncomeToRentRatio]: fieldTypeOperatorOptions,
  [FieldName.GrossIncome]: fieldTypeOperatorOptions,
  [FieldName.CollectionsBalance]: fieldTypeOperatorOptions,
  [FieldName.DebtToNetIncome]: fieldTypeOperatorOptions,
  [FieldName.FelonyRecords]: fieldTypeOperatorOptions
};

export const defaultRule: Omit<Rule, "id" | "result" | "applicant_values"> = {
  field_name: FieldName.GrossIncomeToRentRatio,
  operator: Operator.GreaterThan,
  field_type: "sum",
  value: "2.0"
};

export const defaultRuleSet: RuleSet[] = [
  {
    id: v4(),
    type: RuleSetDecision.Decline,
    rules: []
  },
  {
    id: v4(),
    type: RuleSetDecision.Approve,
    rules: []
  }
];

export const fieldTypeOptions = {
  [FieldName.GrossIncome]: [
    {
      label: "Sum",
      value: "sum"
    }
  ],
  [FieldName.GrossIncomeToRentRatio]: [
    {
      label: "Sum",
      value: "sum"
    }
  ],
  [FieldName.NetIncome]: [
    {
      label: "Sum",
      value: "sum"
    }
  ],
  [FieldName.DebtToNetIncome]: [
    {
      label: "Percentage",
      value: "percentage"
    }
  ],
  [FieldName.UnpaidCollectionsBalance]: [
    {
      label: "Any",
      value: "any"
    }
  ],
  [FieldName.CollectionsBalance]: [
    {
      label: "Any",
      value: "any"
    }
  ],
  [FieldName.BankruptcyRecords]: fieldTypeTimeFrameOptions,
  [FieldName.HousingCourtRecords]: fieldTypeTimeFrameOptions,
  [FieldName.PastDueAccount]: [
    {
      label: "Percentage",
      value: "percentage"
    },
    {
      label: "Numbers",
      value: "numeric"
    }
  ],
  [FieldName.CreditScore]: [
    {
      label: "Group average",
      value: "average"
    },
    {
      label: "Lowest in group",
      value: "min"
    },
    {
      label: "Highest in group",
      value: "max"
    }
  ],
  [FieldName.NoCreditScore]: [
    {
      label: "Anyone in group",
      value: "any"
    },
    {
      label: "Everyone in group",
      value: "all"
    }
  ],
  [FieldName.SexOffenderCheck]: [
    {
      label: "Any",
      value: "any"
    }
  ],
  [FieldName.FelonyRecords]: fieldTypeTimeFrameOptions
};

export const defaultOperator = {
  [FieldName.GrossIncomeToRentRatio]: Operator.GreaterThan,
  [FieldName.DebtToNetIncome]: Operator.GreaterThan,
  [FieldName.NetIncome]: Operator.GreaterThan,
  [FieldName.GrossIncome]: Operator.GreaterThan,
  [FieldName.CreditScore]: Operator.LessThan,
  [FieldName.UnpaidCollectionsBalance]: Operator.GreaterThan,
  [FieldName.CollectionsBalance]: Operator.GreaterThan,
  [FieldName.PastDueAccount]: Operator.GreaterThan,
  [FieldName.BankruptcyRecords]: Operator.GreaterThan,
  [FieldName.HousingCourtRecords]: Operator.GreaterThan,
  [FieldName.SexOffenderCheck]: Operator.Equal,
  [FieldName.NoCreditScore]: Operator.Equal,
  [FieldName.FelonyRecords]: Operator.Equal
};

export const defaultValues = {
  [FieldName.GrossIncomeToRentRatio]: "2.0",
  [FieldName.NetIncome]: "2000",
  [FieldName.GrossIncome]: "5000",
  [FieldName.CreditScore]: "350",
  [FieldName.UnpaidCollectionsBalance]: "200",
  [FieldName.CollectionsBalance]: "200",
  // Must be percentage
  [FieldName.PastDueAccount]: "20",
  [FieldName.DebtToNetIncome]: "20",
  [FieldName.BankruptcyRecords]: "2",
  [FieldName.HousingCourtRecords]: "2",
  [FieldName.SexOffenderCheck]: 1,
  [FieldName.NoCreditScore]: 1,
  [FieldName.FelonyRecords]: "1"
};

export const PERCENTAGE_INPUT_LENGTH = 11;
